import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the browser's default install prompt from showing
      e.preventDefault();
      e.stopImmediatePropagation();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);
    
    // Clear the deferredPrompt for next time
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Save dismissal to localStorage so we don't show it again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if dismissed recently (within 7 days)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (dismissedTime > sevenDaysAgo) {
        setShowInstallPrompt(false);
      }
    }
  }, []); // Empty dependency array - only run once on mount

  if (!showInstallPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <button
      onClick={handleInstallClick}
      onContextMenu={(e) => {
        e.preventDefault();
        handleDismiss();
      }}
      title="Click to install app, right-click to dismiss"
      className={`
        fixed bottom-4 right-4 z-50 
        flex items-center gap-2 px-4 py-3 rounded-lg 
        font-medium text-sm shadow-lg
        transition-all duration-200 hover:scale-105
        ${isDarkMode 
          ? 'bg-yellow-400 text-blue-900 hover:bg-yellow-300' 
          : 'bg-amber-600 text-white hover:bg-amber-700'
        }
      `}
    >
      <Download className="w-4 h-4" />
      <span className="hidden sm:inline">Install Solar Insight SA</span>
      <span className="sm:hidden">Install</span>
    </button>
  );
};

export default PWAInstallButton;
